import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useMemo } from 'react';
import { useUserContext } from '../context/use-user-context';
import { useExcludeOnPatterns } from './useExcludeOnPatterns';
import { useEnableOnPatterns } from './useEnableOnPatterns';
import type { Store } from '../services/types';
import { useChatContext } from '../context/use-chat-context';
import { GrowthbookFeatureFlag } from '../constants/growthbook-feature-flags';
import { getEnvBoolean } from '../../utils/env';

const isAllFeatureFlagsEnabled = getEnvBoolean('ALL_FEATURE_FLAGS_ENABLED');

type UseChatBarVisibilityProps = {
  store?: Store;
};

/**
 * Hook that determines if the chat bar should be visible.
 *
 * Priority (embed / non-Superserve UI):
 * 1. GrowthBook `show-superbar` off or unknown → hide (`useFeatureIsOn`)
 * 2. `goLiveEnabled === false` → hide
 * 3. User not available → hide
 * 4. `enableOnPatterns` set → allowlist URLs only (hide if not matched)
 * 5. `excludeOnPatterns` set → hide on matching URLs (applied after enableOnPatterns check)
 * 6. Otherwise → show
 *
 * Superserve UI always shows the bar (dashboard / preview).
 */
export const useChatBarVisibility = ({ store }: UseChatBarVisibilityProps) => {
  const { isAvailableToCurrentUser, originalIsAvailableToCurrentUser } =
    useUserContext();

  const { isSuperserveUi } = useChatContext();
  const showSuperbarFromGrowthbook = useFeatureIsOn(
    GrowthbookFeatureFlag.SHOW_SUPERBAR
  );
  const { isEnabledOnPatterns, currentUrl: enableUrl } = useEnableOnPatterns({
    store,
  });
  const { isExcludedOnPatterns, currentUrl: excludeUrl } = useExcludeOnPatterns(
    { store }
  );

  const isVisible = useMemo(() => {
    if (isSuperserveUi) return true;

    if (!showSuperbarFromGrowthbook && !isAllFeatureFlagsEnabled) {
      return false;
    }

    if (store?.config?.config?.goLiveEnabled === false) {
      return false;
    }

    // Rule 1: User must be available (only for non-Superserve UI)
    if (!isAvailableToCurrentUser) {
      return false;
    }

    const enablePatterns =
      store?.config?.config?.answerConfig?.enableOnPatterns;
    const excludePatterns =
      store?.config?.config?.answerConfig?.excludeOnPatterns;

    const hasEnablePatterns =
      Array.isArray(enablePatterns) && enablePatterns.length > 0;
    const hasExcludePatterns =
      Array.isArray(excludePatterns) && excludePatterns.length > 0;

    // Rule 2: If enablePatterns are defined, only show on matching URLs (allowlist mode)
    if (hasEnablePatterns && !isEnabledOnPatterns) {
      return false;
    }

    // Rule 3: If excludePatterns are defined, hide on matching URLs (blocklist mode)
    // Applies even when the page passed the enablePatterns check
    if (hasExcludePatterns && isExcludedOnPatterns) {
      return false;
    }

    // Rule 4: Default - show everywhere
    return true;
  }, [
    isAvailableToCurrentUser,
    isEnabledOnPatterns,
    isExcludedOnPatterns,
    store?.config?.config?.answerConfig?.enableOnPatterns,
    store?.config?.config?.answerConfig?.excludeOnPatterns,
    store?.config?.config?.goLiveEnabled,
    isSuperserveUi,
    showSuperbarFromGrowthbook,
  ]);

  return {
    isVisible,
    isAvailableToCurrentUser,
    originalIsAvailableToCurrentUser,
    isEnabledOnPatterns,
    isExcludedOnPatterns,
    currentUrl: enableUrl || excludeUrl,
  };
};
